/* ═══════════════════════════════════════════════════════════════════════════
   RT CART v4 — optimistic cart controller
   New file. Replaces the cart half of rt-cart-shipping.js.
   Load AFTER custom-async.js so the capture-phase interceptors can pre-empt
   the theme's own handlers.

   ── Why this exists ────────────────────────────────────────────────────────
   Measured on the live cart, 03 Sep 2026. One click on the qty stepper made
   the theme:
     • POST /cart/change.js asking for sections "side-cart,<cart section>",
       which returns ~83 KB of HTML (26 KB gzipped),
     • wait 0.9 s to 2.8 s with no visual feedback whatsoever,
     • then innerHTML-replace the ENTIRE cart section and the ENTIRE side-cart
       drawer, re-decoding every image,
     • which re-dispatched semanticInput, so the recommendations rail refetched
       /cart.js plus two /recommendations/products.json calls,
     • plus four /recommendations/products?section_id=side-cart calls.
   Three clicks 320 ms apart produced ~40 requests and ~4 s of churn, and the
   three change.js calls raced: the 778 ms response landed before the 1485 ms
   one, so the cart briefly rendered a stale quantity.

   ── What this does instead ─────────────────────────────────────────────────
     • Paints the new quantity and the new money immediately (optimistic).
     • Debounces 320 ms per line, aborts the superseded in-flight request, and
       stamps every request so a late response can never overwrite a newer one.
     • Asks for NO sections. /cart/change.js then returns ~2 KB of JSON.
     • Patches only the numbers that changed. No innerHTML on the page.
     • Refreshes the summary panel from the server once, 700 ms after the user
       stops interacting, so cart-level discounts stay authoritative.
   ═══════════════════════════════════════════════════════════════════════════ */

(function () {
  'use strict';

  var root = document.querySelector('[data-rt-cart]');
  if (!root) return;

  /* The empty-cart page renders the same wrapper (it needs data-money-format
     and the section id), but there is nothing here to drive: no lines, no
     progress, no summary. Bail before anything binds.

     This also closes an infinite reload loop: boot() calls refreshFromServer(),
     which used to navigate to /cart whenever the server reported an empty cart
     — including on the empty-cart page itself, which then booted and navigated
     again. Liquid stamps `cart-empty` on the wrapper server-side, so it is the
     authoritative signal for "this page was rendered with no items". */
  var RENDERED_EMPTY = root.classList.contains('cart-empty');
  /* The bail itself now happens a little further down, after the money
     helpers are set up, so the empty page can still paint the recently-viewed
     rail. Everything between here and there is declarations only: nothing
     binds, fetches, or navigates, so the loop stays closed. */

  /* ── tuning ─────────────────────────────────────────────────────────── */
  var QTY_DEBOUNCE  = 320;   /* ms to wait after the last click on a line   */
  var SYNC_DEBOUNCE = 700;   /* ms of quiet before the authoritative resync */

  /* ── state ──────────────────────────────────────────────────────────── */
  var seq        = 0;        /* monotonic request stamp                     */
  var applied    = 0;        /* highest stamp whose response we have used   */
  var inflight   = {};       /* line -> AbortController                     */
  var timers     = {};       /* line -> debounce timer                      */
  var syncTimer  = null;
  var busy       = false;    /* true while a mutation is outstanding        */
  var THRESHOLD  = parseInt(root.getAttribute('data-threshold'), 10) || 3;
  var SECTION_ID = root.getAttribute('data-section-id') || '';

  function $(sel, ctx) { return (ctx || root).querySelector(sel); }
  function $$(sel, ctx) { return Array.prototype.slice.call((ctx || root).querySelectorAll(sel)); }

  /* ─────────────────────────────────────────────────────────────── money ── */
  /* Reimplements Shopify's money_format filter so prices can be painted
     without a round trip. Checked against a server-rendered value at boot. */

  var MONEY_FORMAT = root.getAttribute('data-money-format') || '${{amount}}';
  var moneyOk = true;

  function group(intStr, sep) {
    return intStr.replace(/(\d)(?=(\d\d\d)+$)/g, '$1' + sep);
  }

  function partsFor(cents, precision, thousands, decimal) {
    var neg = cents < 0;
    var n = Math.abs(Number(cents) || 0) / 100;
    var bits = n.toFixed(precision).split('.');
    var out = group(bits[0], thousands);
    if (precision > 0) out += decimal + bits[1];
    return (neg ? '-' : '') + out;
  }

  function money(cents) {
    if (!moneyOk) return fallbackMoney(cents);
    return MONEY_FORMAT.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_, token) {
      switch (token) {
        case 'amount':                                  return partsFor(cents, 2, ',', '.');
        case 'amount_no_decimals':                      return partsFor(cents, 0, ',', '.');
        case 'amount_with_comma_separator':             return partsFor(cents, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return partsFor(cents, 0, '.', ',');
        case 'amount_with_apostrophe_separator':        return partsFor(cents, 2, "'", '.');
        case 'amount_with_space_separator':             return partsFor(cents, 2, ' ', ',');
        case 'amount_no_decimals_with_space_separator': return partsFor(cents, 0, ' ', ',');
        default:                                        return partsFor(cents, 2, ',', '.');
      }
    });
  }

  var _intl = null;
  function fallbackMoney(cents) {
    try {
      if (!_intl) {
        var cur = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'INR';
        _intl = new Intl.NumberFormat(document.documentElement.lang || 'en-IN',
                                      { style: 'currency', currency: cur });
      }
      return _intl.format((Number(cents) || 0) / 100);
    } catch (e) {
      return String((Number(cents) || 0) / 100);
    }
  }

  function whenReady(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn);
    else fn();
  }

  if (RENDERED_EMPTY) {
    /* Nothing here to drive, and binding anything re-opens the reload loop
       described above. The recently-viewed rail is the one exception: it is
       read-only, and an empty cart is exactly the moment a customer wants a
       way back to what they were looking at. */
    whenReady(function () { loadSeen([]); });
    return;
  }

  /* ───────────────────────────────────────────────────────── DOM helpers ── */

  function lineEl(line) { return $('[data-rt-line="' + line + '"]'); }
  function lines() { return $$('[data-rt-line]'); }
  function setText(el, txt) { if (el && el.textContent !== txt) el.textContent = txt; }

  function localSubtotal() {
    var sum = 0;
    lines().forEach(function (li) {
      var unit = parseInt(li.getAttribute('data-unit-price'), 10) || 0;
      var input = $('[name="updates[]"]', li);
      sum += unit * (input ? (parseInt(input.value, 10) || 0) : 0);
    });
    return sum;
  }

  function localCount() {
    var n = 0;
    lines().forEach(function (li) {
      var input = $('[name="updates[]"]', li);
      if (input) n += parseInt(input.value, 10) || 0;
    });
    return n;
  }

  /* ──────────────────────────────────────────────────── the unlock moment ── */
  /* Crossing the 3-item threshold is the one genuinely rewarding event on this
     page, so it is worth marking. But the reward is the money coming down, not
     decoration laid on top of it: the total counts down to its new value while
     the rail springs to full and turns green, the mark pops and draws its
     check, and the card washes green and settles back. ~950 ms, then still.

     Deliberately NOT here: the old full-viewport confetti canvas (120 pieces on
     a rAF physics loop) and the "3% Discount Unlocked! 🎉" toast. Those read as
     aftermarket hype; see rt-copy-tone.

     Fires only on a live locked → unlocked transition, never on page load, and
     never on the way back down. Losing a reward should not be dramatised. */

  var UNLOCK_KEY  = 'rt_disc_unlocked';
  var _prevTotal  = null;   /* last painted total, so we know where to count from */
  var _booted     = false;  /* suppress the celebration on the first paint        */

  function reducedMotion() {
    try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; }
    catch (e) { return false; }
  }
  function wasUnlocked()  { try { return sessionStorage.getItem(UNLOCK_KEY) === '1'; } catch (e) { return false; } }
  function setUnlocked(v) { try { sessionStorage.setItem(UNLOCK_KEY, v ? '1' : '0'); } catch (e) {} }

  /* Count a money element from one value to another. */
  function animateMoney(el, fromCents, toCents, ms) {
    if (!el) return;
    if (reducedMotion() || fromCents === toCents || !window.requestAnimationFrame) {
      el.textContent = money(toCents);
      return;
    }
    var start = null, delta = toCents - fromCents, settled = false;
    el.classList.add('is-ticking');

    function settle() {
      if (settled) return;
      settled = true;
      el.textContent = money(toCents);
      el.classList.remove('is-ticking');
    }
    /* If rAF is throttled (background tab, reduced power mode) the loop below
       may never run. Snap to the final value regardless. */
    setTimeout(settle, ms + 250);

    function step(ts) {
      if (settled) return;
      if (start === null) start = ts;
      var t = Math.min(1, (ts - start) / ms);
      var eased = 1 - Math.pow(1 - t, 3);           /* ease-out cubic */
      el.textContent = money(Math.round(fromCents + delta * eased));
      if (t < 1) requestAnimationFrame(step);
      else settle();
    }
    requestAnimationFrame(step);
  }

  /* ── the burst ──────────────────────────────────────────────────────────
     The old version ran a 120-piece canvas with a requestAnimationFrame
     physics loop on the main thread. This does the same job with ~18 DOM
     nodes, one CSS animation each, animating nothing but transform and
     opacity, so the whole thing runs on the compositor. Each piece gets its
     own trajectory through custom properties, so all of them share a single
     @keyframes rule. The layer is removed from the DOM when it finishes. */

  var BURST_COLORS = ['#e63946', '#15803d', '#0a0a0a', '#f0a500'];

  function particleCount() {
    var n = 24;
    try {
      /* Halve it on low-spec Android, which is a real slice of this traffic. */
      if (navigator.deviceMemory && navigator.deviceMemory <= 4) n = 10;
      if (navigator.hardwareConcurrency && navigator.hardwareConcurrency <= 4) n = Math.min(n, 10);
    } catch (e) {}
    return n;
  }

  function burst(box) {
    var mark = box.querySelector('.rt-progress__mark');
    if (!mark) return;

    var layer = document.createElement('div');
    layer.className = 'rt-burst';
    layer.setAttribute('aria-hidden', 'true');

    /* Anchor the layer on the mark, measured once. */
    var mr = mark.getBoundingClientRect(), br = box.getBoundingClientRect();
    layer.style.left = (mr.left - br.left + mr.width / 2) + 'px';
    layer.style.top = (mr.top - br.top + mr.height / 2) + 'px';

    var n = particleCount();
    var frag = document.createDocumentFragment();
    for (var i = 0; i < n; i++) {
      var p = document.createElement('i');
      /* Fan across the upper half: -160deg to -20deg. */
      var ang = (-160 + (140 * (i / (n - 1)))) * Math.PI / 180;
      var dist = 78 + Math.random() * 86;
      p.style.setProperty('--tx', Math.round(Math.cos(ang) * dist) + 'px');
      p.style.setProperty('--pk', Math.round(Math.sin(ang) * dist) + 'px');
      p.style.setProperty('--ty', Math.round(74 + Math.random() * 56) + 'px');
      p.style.setProperty('--r', Math.round(Math.random() * 540 - 270) + 'deg');
      p.style.setProperty('--d', Math.round(Math.random() * 90) + 'ms');
      p.style.setProperty('--c', BURST_COLORS[i % BURST_COLORS.length]);
      if (i % 3 === 0) p.className = 'is-round';
      frag.appendChild(p);
    }
    layer.appendChild(frag);
    box.appendChild(layer);

    setTimeout(function () {
      if (layer.parentNode) layer.parentNode.removeChild(layer);
    }, 1400);
  }

  function celebrate() {
    var box = $('[data-rt-progress]');
    if (!box || reducedMotion()) return;

    burst(box);

    box.classList.remove('rt-progress--celebrate');
    void box.offsetWidth;                       /* restart if it fires twice */
    box.classList.add('rt-progress--celebrate');

    /* Draw the check along its real length rather than guessing a dasharray. */
    var path = box.querySelector('.rt-progress__mark svg path');
    if (path && path.getTotalLength && path.animate) {
      try {
        var len = path.getTotalLength();
        path.style.strokeDasharray = len;
        var anim = path.animate(
          [{ strokeDashoffset: len }, { strokeDashoffset: 0 }],
          { duration: 340, delay: 220, easing: 'cubic-bezier(.32,.72,0,1)', fill: 'both' }
        );
        anim.onfinish = function () {
          path.style.strokeDasharray = '';
          path.style.strokeDashoffset = '';
          try { anim.cancel(); } catch (e) {}
        };
      } catch (e) { /* decorative only */ }
    }

    /* A single short tap on mobile. Nothing on desktop. */
    try { if (navigator.vibrate) navigator.vibrate(12); } catch (e) {}

    setTimeout(function () { box.classList.remove('rt-progress--celebrate'); }, 1200);
  }

  /* ─────────────────────────────────────────────────────────────── paint ── */

  /* Returns true when this paint crossed the threshold for the first time. */
  function paintProgress(count, savedCents, authoritative) {
    var box = $('[data-rt-progress]');
    if (!box) return false;
    var done = count >= THRESHOLD;
    var left = Math.max(0, THRESHOLD - count);
    var pct = Math.min(100, Math.round((count / THRESHOLD) * 100));

    /* Only the server-confirmed paint may consume the transition. The
       optimistic paint runs first and does not yet know the discount, so if it
       were allowed to claim the unlock the celebration would fire on the paint
       where the total goes UP, and the real one would be suppressed. */
    var justUnlocked = false;
    if (authoritative) {
      justUnlocked = _booted && done && !wasUnlocked();
      setUnlocked(done);
    }
    box.classList.toggle('rt-progress--done', done);

    var fill = $('[data-rt-progress-fill]', box);
    if (fill) fill.style.width = pct + '%';

    var rail = box.querySelector('[role="progressbar"]');
    if (rail) rail.setAttribute('aria-valuenow', String(count));

    setText($('[data-rt-progress-tally]', box), count + ' / ' + THRESHOLD);

    var msg = $('[data-rt-progress-msg]', box);
    if (msg) {
      if (done) {
        /* Name the actual rupees. "3% off" is abstract; the amount is not.
           savedCents is subtotal minus total, so it covers every cart-level
           discount in play, not only the multi-item one. */
        msg.innerHTML = savedCents > 0
          ? '<strong>3% off applied.</strong> You are saving ' + money(savedCents) + ' on this order.'
          : '<strong>3% off applied.</strong> It comes off automatically at checkout.';
      } else {
        msg.innerHTML = 'Add <strong>' + left + ' more ' + (left === 1 ? 'item' : 'items') +
                        '</strong> to take 3% off everything.';
      }
    }
    return justUnlocked;
  }

  function paintTotals(count, subtotalCents, totalCents, authoritative) {
    var saved = Math.max(0, subtotalCents - totalCents);
    var justUnlocked = paintProgress(count, saved, authoritative);

    setText($('[data-rt-count]'), String(count));
    setText($('[data-rt-subtotal]'), money(subtotalCents));

    if (justUnlocked) {
      celebrate();
      if (_prevTotal !== null && _prevTotal > totalCents) {
        animateMoney($('[data-rt-total]'), _prevTotal, totalCents, 640);
        animateMoney($('[data-rt-bar-total]'), _prevTotal, totalCents, 640);
      } else {
        setText($('[data-rt-total]'), money(totalCents));
        setText($('[data-rt-bar-total]'), money(totalCents));
      }
    } else {
      setText($('[data-rt-total]'), money(totalCents));
      setText($('[data-rt-bar-total]'), money(totalCents));
    }
    _prevTotal = totalCents;

    setText($('[data-rt-itemcount]'), count + ' ' + (count === 1 ? 'item' : 'items'));
    root.setAttribute('data-totalqty', String(count));
    root.setAttribute('data-totalprice', money(totalCents));
    root.setAttribute('data-total-cents', String(totalCents));

    /* Patch the header bubble directly. The theme refetched the whole
       side-cart section just to update this number. */
    var bubble = document.getElementById('cart-count');
    if (bubble) bubble.textContent = String(count);
    var totalEl = document.getElementById('cart-total');
    if (totalEl) totalEl.textContent = money(totalCents);
  }

  var _knownDiscount = 0;

  /* Optimistic: recompute from what is already on screen. The cart-level
     discount is unknowable locally, so carry the last known one forward
     rather than inventing a number. */
  function paintOptimistic() {
    var sub = localSubtotal();
    paintTotals(localCount(), sub, Math.max(0, sub - _knownDiscount), false);
  }

  /* Authoritative: everything from the cart JSON. */
  function paintFromCart(cart) {
    _knownDiscount = (cart.items_subtotal_price || 0) - (cart.total_price || 0);

    (cart.items || []).forEach(function (item, i) {
      var li = lineEl(i + 1);
      if (!li) return;
      li.setAttribute('data-unit-price', String(item.final_price));

      var input = $('[name="updates[]"]', li);
      if (input && document.activeElement !== input) input.value = item.quantity;

      setText($('[data-rt-now]', li), money(item.final_line_price));

      var was = $('[data-rt-was]', li);
      if (was) {
        if (item.original_line_price && item.original_line_price > item.final_line_price) {
          was.textContent = money(item.original_line_price);
          was.hidden = false;
        } else {
          was.hidden = true;
        }
      }

      var unit = $('[data-rt-unit]', li);
      if (unit) {
        if (item.quantity > 1) {
          unit.textContent = money(item.final_price) + ' each';
          unit.hidden = false;
        } else {
          unit.hidden = true;
        }
      }

      syncStepper(li, item.quantity);
    });

    paintTotals(cart.item_count, cart.items_subtotal_price, cart.total_price, true);
  }

  function syncStepper(li, qty) {
    var input = $('[name="updates[]"]', li);
    if (!input) return;
    var min = parseInt(input.getAttribute('min'), 10) || 1;
    var maxAttr = input.getAttribute('max');
    var max = maxAttr ? parseInt(maxAttr, 10) : Infinity;
    var dec = $('[data-rt-step="-1"]', li);
    var inc = $('[data-rt-step="1"]', li);
    if (dec) dec.disabled = qty <= min;
    if (inc) inc.disabled = qty >= max;
  }

  /* ───────────────────────────────────────────────────────────── network ── */

  function post(url, body, signal) {
    return fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      signal: signal,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(body)
    }).then(function (r) {
      return r.json().then(function (j) {
        if (!r.ok) { var e = new Error((j && j.description) || 'Cart error'); e.cart = j; throw e; }
        return j;
      });
    });
  }

  function alertUser(msg) {
    if (!msg) return;
    try {
      window.dispatchEvent(new CustomEvent('showAlert', { detail: { message: msg, type: 'error' } }));
    } catch (e) { /* the theme's alert bus is optional */ }
  }

  function clearSyncing() {
    $$('.rt-line--syncing').forEach(function (el) { el.classList.remove('rt-line--syncing'); });
  }

  /* Commit one line's quantity. Debounced, abortable, sequence-guarded. */
  function commit(line, qty) {
    clearTimeout(timers[line]);
    timers[line] = setTimeout(function () {
      if (inflight[line]) { try { inflight[line].abort(); } catch (e) {} }
      var ctrl = ('AbortController' in window) ? new AbortController() : null;
      inflight[line] = ctrl;

      var stamp = ++seq;
      busy = true;

      post('/cart/change.js', { line: line, quantity: qty }, ctrl && ctrl.signal)
        .then(function (cart) {
          /* A slower earlier request must never repaint over a newer one. */
          if (stamp < applied) return;
          applied = stamp;
          if (cart.item_count === 0) { goEmptyCart(); return; }
          paintFromCart(cart);
          scheduleSync();
        })
        .catch(function (err) {
          if (err && err.name === 'AbortError') return;
          alertUser((err && err.message) || 'Could not update the cart.');
          refreshFromServer();  /* never leave an optimistic lie on screen */
        })
        .then(function () {
          if (inflight[line] === ctrl) delete inflight[line];
          if (!Object.keys(inflight).length) { busy = false; clearSyncing(); }
        });
    }, QTY_DEBOUNCE);
  }

  /* Navigate to the empty-cart page, but never from a page that is already
     showing it — that is the reload loop described at the top of this file. */
  function goEmptyCart() {
    if (!RENDERED_EMPTY) window.location.assign('/cart');
  }

  function refreshFromServer() {
    return fetch('/cart.js', { credentials: 'same-origin', headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (cart.item_count === 0) { goEmptyCart(); return; }
        paintFromCart(cart);
      })
      .catch(function () {});
  }

  /* Once the dust settles, pull the real summary. This is the only section
     fetch left; it happens at most once per burst and swaps ONE subtree. */
  function scheduleSync() {
    clearTimeout(syncTimer);
    syncTimer = setTimeout(function () {
      if (busy || !SECTION_ID) return;
      fetch(window.location.pathname + '?section_id=' + encodeURIComponent(SECTION_ID),
            { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.text() : Promise.reject(); })
        .then(function (html) {
          if (busy) return;  /* the customer started clicking again */
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var fresh = doc.querySelector('[data-rt-totals]');
          var here = $('[data-rt-totals]');
          if (fresh && here && fresh.innerHTML !== here.innerHTML) here.innerHTML = fresh.innerHTML;

          var freshTotal = doc.querySelector('[data-rt-total]');
          if (freshTotal) {
            var t = freshTotal.textContent.trim();
            setText($('[data-rt-total]'), t);
            setText($('[data-rt-bar-total]'), t);
          }
        })
        .catch(function () {});
    }, SYNC_DEBOUNCE);
  }

  /* ────────────────────────────────────────────────────────── quantity UI ── */

  function stepLine(li, delta, absolute) {
    var input = $('[name="updates[]"]', li);
    if (!input) return;
    var line = parseInt(input.getAttribute('data-line'), 10);
    var min = parseInt(input.getAttribute('min'), 10) || 1;
    var maxAttr = input.getAttribute('max');
    var max = maxAttr ? parseInt(maxAttr, 10) : Infinity;
    var stepBy = parseInt(input.getAttribute('step'), 10) || 1;

    var next = (absolute != null)
      ? absolute
      : (parseInt(input.value, 10) || min) + (delta * stepBy);
    next = Math.max(min, Math.min(max, next));
    if (absolute == null && String(next) === String(input.value)) return;

    /* 1. paint now */
    input.value = next;
    syncStepper(li, next);

    var unitCents = parseInt(li.getAttribute('data-unit-price'), 10) || 0;
    setText($('[data-rt-now]', li), money(unitCents * next));

    var unitEl = $('[data-rt-unit]', li);
    if (unitEl) {
      if (next > 1) { unitEl.textContent = money(unitCents) + ' each'; unitEl.hidden = false; }
      else { unitEl.hidden = true; }
    }

    li.classList.add('rt-line--syncing');
    paintOptimistic();

    /* 2. tell the server later */
    commit(line, next);
  }

  function removeLine(li) {
    var input = $('[name="updates[]"]', li);
    var link = $('[data-rt-remove]', li);
    var line = parseInt((input && input.getAttribute('data-line')) ||
                        (link && link.getAttribute('data-line')), 10);
    if (!line) return;

    /* Collapse the row first so the list does not jump a second later when
       the response lands. */
    li.style.height = li.getBoundingClientRect().height + 'px';
    li.classList.add('rt-line--leaving');
    window.setTimeout(function () {
      li.classList.add('rt-line--collapse');
      window.setTimeout(function () { if (li.parentNode) li.parentNode.removeChild(li); }, 300);
    }, 190);

    var stamp = ++seq;
    busy = true;
    post('/cart/change.js', { line: line, quantity: 0 }, null)
      .then(function (cart) {
        if (stamp < applied) return;
        applied = stamp;
        if (cart.item_count === 0) { goEmptyCart(); return; }
        renumber();
        paintFromCart(cart);
        scheduleSync();
      })
      .catch(function (err) {
        alertUser((err && err.message) || 'Could not remove that item.');
        window.location.assign('/cart');
      })
      .then(function () { busy = false; clearSyncing(); });
  }

  /* Shopify addresses lines by 1-based position, so the remaining rows must be
     renumbered after a removal or the next edit hits the wrong item. */
  function renumber() {
    lines().forEach(function (li, i) {
      var n = String(i + 1);
      li.setAttribute('data-rt-line', n);
      var input = $('[name="updates[]"]', li);
      if (input) input.setAttribute('data-line', n);
      var rm = $('[data-rt-remove]', li);
      if (rm) rm.setAttribute('data-line', n);
    });
  }

  /* ─────────────────────────────────────────────────────── event plumbing ── */

  /* The theme binds `change` DIRECTLY on input[name="updates[]"] inside
     handleCartUpdates(), with no opt-out hook. A capture-phase listener on the
     document stops the event before it ever reaches the target, so the theme's
     heavy handler never runs here. If this file fails to load, the theme's
     handler still works — degraded, but not broken. */
  document.addEventListener('change', function (e) {
    var input = e.target;
    if (!input || input.name !== 'updates[]' || !root.contains(input)) return;
    e.stopPropagation();
    var li = input.closest('[data-rt-line]');
    if (!li) return;
    var v = parseInt(input.value, 10);
    if (isNaN(v) || v < 1) v = 1;
    stepLine(li, 0, v);
  }, true);

  /* Same for the order note: the theme's version refetched both sections. */
  document.addEventListener('change', function (e) {
    var el = e.target;
    if (!el || el.name !== 'note' || !root.contains(el)) return;
    e.stopPropagation();
    post('/cart/update.js', { note: el.value }, null).catch(function () {});
  }, true);

  /* Discount add, intercepted before the theme's full re-render. */
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || !form.matches('[data-rt-discount]') || !root.contains(form)) return;
    e.preventDefault();
    e.stopPropagation();
    applyDiscount(form);
  }, true);

  /* ONE capture-phase click handler for every control on this page.
     Do not add a second document-level click listener that also calls
     stepLine — every click would then count twice. */
  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;

    var rm = t.closest('[data-rt-remove]');
    if (rm && root.contains(rm)) {
      e.preventDefault();
      e.stopPropagation();
      var li = rm.closest('[data-rt-line]');
      if (li && !li.classList.contains('rt-line--leaving')) removeLine(li);
      return;
    }

    var stepBtn = t.closest('[data-rt-step]');
    if (stepBtn && root.contains(stepBtn)) {
      e.preventDefault();
      var row = stepBtn.closest('[data-rt-line]');
      if (row) stepLine(row, parseInt(stepBtn.getAttribute('data-rt-step'), 10), null);
      return;
    }

    var del = t.closest('.remove-discount');
    if (del && root.contains(del)) {
      e.preventDefault();
      e.stopPropagation();
      removeDiscount(del.getAttribute('data-discount-code'));
      return;
    }

    var add = t.closest('[data-rt-add]');
    if (add) {
      e.preventDefault();
      e.stopPropagation();
      addUpsell(add);
      return;
    }
  }, true);

  /* ──────────────────────────────────────────────────────────── discounts ── */

  function currentCodes() {
    return $$('.remove-discount').map(function (a) {
      return (a.getAttribute('data-discount-code') || '').toLowerCase();
    }).filter(Boolean);
  }

  function showCodeError(sel) {
    $$('.rt-code__err').forEach(function (n) { n.classList.add('hidden'); });
    var n = $(sel);
    if (n) n.classList.remove('hidden');
  }

  function applyDiscount(form) {
    var input = $('input[name="discount"]', form);
    var btn = $('button', form);
    var code = ((input && input.value) || '').trim();
    if (!code) return;

    var codes = currentCodes();
    if (codes.indexOf(code.toLowerCase()) !== -1) {
      showCodeError('.already-applied-error-message');
      return;
    }
    codes.push(code);

    if (btn) btn.classList.add('is-busy');
    $$('.rt-code__err').forEach(function (n) { n.classList.add('hidden'); });

    post('/cart/update.js', { discount: codes.join(',') }, null)
      .then(function (cart) {
        var hit = (cart.discount_codes || []).filter(function (d) {
          return d.code.toLowerCase() === code.toLowerCase();
        })[0];
        if (input) input.value = '';
        if (hit && hit.applicable === false) { showCodeError('.not-applicable-error-message'); return; }
        paintFromCart(cart);
        forceSync();
      })
      .catch(function (err) { alertUser((err && err.message) || 'That code could not be applied.'); })
      .then(function () { if (btn) btn.classList.remove('is-busy'); });
  }

  function removeDiscount(code) {
    if (!code) return;
    var codes = currentCodes().filter(function (c) { return c !== code.toLowerCase(); });
    post('/cart/update.js', { discount: codes.join(',') }, null)
      .then(function (cart) { paintFromCart(cart); forceSync(); })
      .catch(function (err) { alertUser((err && err.message) || 'Could not remove that discount.'); });
  }

  function forceSync() { busy = false; scheduleSync(); }

  /* ─────────────────────────────────────────────────────── checkout guard ── */

  var checkoutForm = $('[data-rt-checkout-form]');
  if (checkoutForm) {
    checkoutForm.addEventListener('submit', function (e) {
      var terms = $('[data-rt-terms] input[type="checkbox"]', checkoutForm);
      var errBox = $('[data-rt-error]');
      var errTxt = $('[data-rt-error-text]');

      if (terms && !terms.checked) {
        e.preventDefault();
        if (errBox && errTxt) {
          errTxt.textContent = 'Please accept the terms to continue to checkout.';
          errBox.classList.add('is-shown');
        }
        var wrap = $('[data-rt-terms]');
        if (wrap) wrap.classList.add('rt-terms--error');
        terms.focus();
        return;
      }

      /* If a quantity edit is still in flight, let it land so checkout never
         opens against a stale cart. */
      if (busy) {
        e.preventDefault();
        var btn = $('[data-rt-checkout]');
        if (btn) btn.classList.add('is-busy');
        var wait = setInterval(function () {
          if (busy) return;
          clearInterval(wait);
          if (btn) btn.classList.remove('is-busy');
          checkoutForm.submit();
        }, 90);
        setTimeout(function () {
          clearInterval(wait);
          if (btn) btn.classList.remove('is-busy');
        }, 6000);
      }
    });

    var termsBox = $('[data-rt-terms] input[type="checkbox"]', checkoutForm);
    if (termsBox) {
      termsBox.addEventListener('change', function () {
        if (!termsBox.checked) return;
        var errBox = $('[data-rt-error]');
        if (errBox) errBox.classList.remove('is-shown');
        var wrap = $('[data-rt-terms]');
        if (wrap) wrap.classList.remove('rt-terms--error');
      });
    }
  }

  /* ──────────────────────────────────────────────────── sticky mobile bar ── */

  (function stickyBar() {
    var bar = $('[data-rt-bar]');
    var anchor = $('[data-rt-checkout]');
    if (!bar || !anchor || !('IntersectionObserver' in window)) return;
    bar.hidden = false;
    new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) { bar.classList.toggle('is-shown', !entry.isIntersecting); });
    }, { rootMargin: '0px 0px -40px 0px', threshold: 0 }).observe(anchor);
  }());

  /* ────────────────────────────────────────────────── complete your build ── */

  var upsellWrap = document.getElementById('rt-upsell-wrap');
  var upsellGrid = document.getElementById('rt-upsell-grid');
  var upsellLoaded = false;

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function inCartIds() {
    var raw = (upsellWrap && upsellWrap.getAttribute('data-cart-pids')) || '';
    return raw.split(',').map(function (s) { return s.trim(); }).filter(Boolean);
  }

  /* -- money, across two endpoints that disagree --------------------------
     /recommendations/products.json returns variant price as a NUMBER in paise
     (85000 = Rs 850). /search/suggest.json returns it as a STRING in major
     units ("7161.00"). The old code multiplied everything by 100, which turned
     a Rs 5,850 brake pad into a Rs 585,000 one on the live rail. */
  function toPaise(v) {
    if (v == null) return 0;
    if (typeof v === 'number') return Math.round(v);          /* already minor */
    var str = String(v).trim();
    if (/^\d+$/.test(str)) return Math.round(Number(str));    /* "85000"       */
    return Math.round(parseFloat(str) * 100);                 /* "7161.00"     */
  }

  /* -- working out what a product actually IS -----------------------------
     product_type is null across much of this catalogue, so fall back to the
     title. Longest phrases first, so "brake disc" wins before "brake". */
  var CATEGORIES = [
    'brake pad', 'brake disc', 'brake rotor', 'brake line', 'brake fluid',
    'air filter', 'oil filter', 'spark plug', 'engine oil', 'paddock stand',
    'suspension', 'exhaust', 'clutch', 'coolant', 'additive', 'lubricant',
    'cleaner', 'battery', 'helmet', 'chain', 'tyre', 'guard', 'mirror',
    'luggage', 'light', 'horn', 'grip'
  ];
  function categoryOf(product) {
    var t = (product.type || product.product_type || '').toLowerCase();
    var i;
    for (i = 0; i < CATEGORIES.length; i++) {
      if (t.indexOf(CATEGORIES[i]) !== -1) return CATEGORIES[i];
    }
    var title = (product.title || '').toLowerCase();
    for (i = 0; i < CATEGORIES.length; i++) {
      if (title.indexOf(CATEGORIES[i]) !== -1) return CATEGORIES[i];
    }
    return null;
  }

  /* Which end of the vehicle a part is for, when it is axle-specific.
     A listing that covers BOTH ("CRANK1 Brake Pads ... Front & Rear") already
     sells the pair, so it counts as neither.

     Tags first: this catalogue tags brake parts "Front" or "Rear" explicitly
     and that is far more reliable than the title. "DBA Brake Pads for VW
     POLO/POLO GT/VENTO after 2012" carries no axle word in its title at all,
     so a title-only read would miss it. Fall back to the title for the
     products that were never tagged. */
  function axleOf(product) {
    var tags = lowerAll(product.tags || []);
    var tf = tags.indexOf('front') !== -1, tr = tags.indexOf('rear') !== -1;
    if (tf !== tr) return tf ? 'front' : 'rear';

    var s = ((product.title || '') + ' ' +
             (product.type || product.product_type || '')).toLowerCase();
    var f = /\bfront\b/.test(s), r = /\brear\b/.test(s);
    if (f === r) return null;
    return f ? 'front' : 'rear';
  }

  /* -- attachment bias ----------------------------------------------------
     From the last 50 orders, only 8 held more than one product, and the spark
     plug turns up in 5 of those 8; an air filter in 3. Those are the store's
     real add-ons, so nudge them above an equally-scoring random accessory.
     Oil filter and engine oil are inferred from the same servicing habit,
     not measured, hence the smaller weights. */
  var ATTACHMENT_BIAS = {
    'spark plug': 15,
    'air filter': 10,
    'oil filter': 8,
    'engine oil': 5
  };

  /* A tag counts as vehicle-ish if it carries a number (model years and engine
     sizes: "SKODA RAPID/FABIA after 2012", "Himalayan 450") or uses the store's
     vehicle- prefix. Make and category tags are plain words. */
  function isVehicleTag(t) { return /\d/.test(t) || t.indexOf('vehicle-') === 0; }

  function lowerAll(a) {
    return (a || []).map(function (x) { return String(x).toLowerCase(); });
  }

  /* -- what is already in the basket ---------------------------------------
     Read per-ITEM, not as one flat blob, because a cart can hold parts for
     more than one vehicle. main-cart.liquid emits a JSON block; the old flat
     data-cart-* attributes are still read as a fallback. */
  function cartContext() {
    var items = [];
    var node = document.getElementById('rt-cart-context');
    if (node) {
      try {
        var parsed = JSON.parse(node.textContent || '{}');
        (parsed.items || []).forEach(function (it) {
          if (!it || !it.id) return;
          for (var i = 0; i < items.length; i++) {
            if (items[i].id === it.id) return;          /* same product twice */
          }
          items.push({
            id: it.id,
            tags: lowerAll(it.tags),
            type: it.type || '',
            title: it.title || ''
          });
        });
      } catch (e) { /* fall through to the attributes below */ }
    }

    if (!items.length) {
      /* Fallback: one pseudo-item carrying every tag in the cart. */
      var flat = lowerAll(((upsellWrap && upsellWrap.getAttribute('data-cart-tags')) || '').split('|'))
                   .filter(Boolean);
      items.push({
        id: parseInt((upsellWrap && upsellWrap.getAttribute('data-product-id')) || '0', 10),
        tags: flat,
        type: ''
      });
    }

    /* Titles carry the category, since product_type is null across much of
       this catalogue. Fall back to the rendered rows when the JSON block is
       not there. */
    var lineTitles = lines().map(function (li) {
      var a = li.querySelector('.rt-line__title a');
      return a ? a.textContent : '';
    });

    var allTags = [], cats = {};
    items.forEach(function (it, idx) {
      it.vehicleTags = it.tags.filter(isVehicleTag);
      it.searchTerm = bestSearchTerm(it.vehicleTags);
      allTags = allTags.concat(it.vehicleTags);

      /* EACH item gets its own category. This is per-vehicle on purpose: if
         the basket holds a car air filter and a CB350 shock, another car air
         filter is useless, but a CB350 air filter is a perfectly good
         suggestion. A single global category set cannot tell those apart. */
      var probe = {
        type: it.type,
        title: it.title || lineTitles[idx] || '',
        tags: it.tags
      };
      it.category = categoryOf(probe);
      it.axle = axleOf(probe);
      if (it.category) cats[it.category] = 1;
    });

    return {
      items: items,
      vehicleTags: allTags,
      categories: Object.keys(cats),
      skip: inCartIds()
    };
  }

  /* Which tag to actually search on. Not every vehicle-ish tag is usable:
       "vehicle-car"   - a class, matches the whole car catalogue
       "FB01180"       - a part number
       "XL6"           - a real model, but too short to search safely
       "fits-CB 350RS" - what we want
     Prefer tags that read like a model (a space or slash AND a digit), then
     fall back to the longest remaining tag. */
  function bestSearchTerm(vehicleTags) {
    var usable = vehicleTags.filter(function (t) {
      return t.indexOf('vehicle-') !== 0 && t.length > 6;
    });
    /* Drop part numbers: "fb01180", "p85121x". Four or more digits after one
       to three letters is a SKU, not a model. "cb350rs" survives (three
       digits, trailing letters), which is what we want. */
    usable = usable.filter(function (t) { return !/^[a-z]{1,3}\d{4,}[a-z]?$/.test(t); });
    if (!usable.length) return null;
    var modelish = usable.filter(function (t) { return /[\s/]/.test(t) && /\d/.test(t); });
    var pick = (modelish.length ? modelish : usable)
      .sort(function (a, b) { return b.length - a.length; })[0];
    return pick || null;
  }

  function imageUrlOf(p) {
    var candidates = [
      p.featured_image,
      p.image,
      (p.images || [])[0],
      (p.media || [])[0]
    ];
    for (var i = 0; i < candidates.length; i++) {
      var c = candidates[i];
      if (!c) continue;
      var url = (typeof c === 'string')
        ? c
        : (c.url || c.src || (c.preview_image && (c.preview_image.url || c.preview_image.src)) || '');
      url = String(url || '');
      if (!url) continue;
      if (url.indexOf('//') === 0) url = 'https:' + url;
      /* drop any size suffix already on the filename before adding ours */
      url = url.replace(/_(\d+)x(\d*)(?=\.[a-z]+(\?|$))/i, '');
      return url.replace(/(\.(jpg|jpeg|png|webp|gif))/i, '_360x$1');
    }
    return '';
  }

  function fetchJSON(url) {
    return fetch(url, { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });
  }

  /* ─────────────────────────────────────────────── horizontal rail nav ──
     Both rails are plain overflow-x scrollers with scroll snap. That reads as
     a static row of clipped cards on a desktop with no visible scrollbar: the
     theme's own carousel directly below has arrows, so the absence looked
     like a bug rather than a choice.

     The arrows are progressive enhancement. They appear only when the content
     actually overflows, and the edge fade is a mask on the scroller itself
     rather than an overlay, so it can never swallow a click on a card. */
  function wireRail(grid) {
    if (!grid || grid.__railWired) return;
    grid.__railWired = true;

    var section = grid.closest ? grid.closest('.rt-upsell') : null;
    var nav = section && section.querySelector('.rt-upsell__nav');
    var prev = nav && nav.querySelector('[data-rt-scroll="-1"]');
    var next = nav && nav.querySelector('[data-rt-scroll="1"]');
    var ticking = false;

    function paint() {
      var max = grid.scrollWidth - grid.clientWidth;
      var x = grid.scrollLeft;
      var overflows = max > 4;

      if (nav) nav.hidden = !overflows;
      grid.classList.toggle('is-fade-l', overflows && x > 4);
      grid.classList.toggle('is-fade-r', overflows && x < max - 4);
      if (prev) prev.disabled = x <= 4;
      if (next) next.disabled = x >= max - 4;
    }
    /* Coalesce bursts of scroll and resize into a single repaint.

       rAF on its own is not enough. It is parked in a background tab, so a
       frame requested just before the tab is hidden never arrives, and a
       plain boolean guard latches on and drops every later update until the
       tab is looked at again. Arm a timer alongside it and let whichever
       lands first do the work, so the flag can never stick. */
    function schedule() {
      if (ticking) return;
      ticking = true;
      function run() { ticking = false; paint(); }
      var t = setTimeout(run, 120);
      requestAnimationFrame(function () { clearTimeout(t); run(); });
    }

    grid.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    if (window.ResizeObserver) new ResizeObserver(schedule).observe(grid);

    if (nav) {
      nav.addEventListener('click', function (ev) {
        var btn = ev.target.closest('[data-rt-scroll]');
        if (!btn) return;
        ev.preventDefault();
        var dir = parseInt(btn.getAttribute('data-rt-scroll'), 10) || 1;
        var card = grid.firstElementChild;
        /* One card plus its gap, times however many are on screen, so a click
           advances a full screenful and never leaves a half card behind. */
        var step = card ? card.getBoundingClientRect().width + 12 : 188;
        var page = Math.max(1, Math.floor(grid.clientWidth / step)) * step;
        var reduce = window.matchMedia &&
                     window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        grid.scrollBy({ left: dir * page, behavior: reduce ? 'auto' : 'smooth' });
      });
    }

    paint();
  }

  /* ───────────────────────────────────────────────── recently viewed ──
     The theme already keeps this history, in localStorage under
     `recentlyViewedProduct`, as [{productUrl, productId}] oldest first. Read
     the same key rather than starting a second one, so the two can never
     disagree about what the customer looked at.

     What we do NOT reuse is how the theme renders it. Its section fetches
     `?section_id=product-item` once PER PRODUCT, which on this page meant six
     full section documents just to draw six cards, and it draws them with the
     collection-page card: review stars, EMI flags, quick-buy. Stacked under
     the cart's own rail it read as two different websites. We fetch
     /products/<handle>.js instead, a few KB each, and render them through the
     same cardHTML() as the rail above. */
  function recentlyViewed() {
    var raw;
    try { raw = JSON.parse(localStorage.getItem('recentlyViewedProduct') || 'null'); }
    catch (e) { return []; }                       /* private mode, or junk */
    if (!raw || !raw.length) return [];
    return raw.slice().reverse();                  /* stored oldest first */
  }

  function loadSeen(excludeIds) {
    var wrap = document.getElementById('rt-seen-wrap');
    var grid = document.getElementById('rt-seen-grid');
    if (!wrap || !grid) return;

    var skip = inCartIds().concat((excludeIds || []).map(String));
    var entries = recentlyViewed().filter(function (e) {
      return e && e.productUrl && skip.indexOf(String(e.productId)) === -1;
    }).slice(0, 6);
    if (!entries.length) return;                   /* stays hidden */

    Promise.all(entries.map(function (e) {
      var url = String(e.productUrl).split('?')[0].split('#')[0].replace(/\/$/, '');
      return fetchJSON(url + '.js');
    })).then(function (products) {
      var html = products.filter(function (p) {
        return p && p.id && p.available !== false &&
               skip.indexOf(String(p.id)) === -1;
      }).map(cardHTML).filter(Boolean).join('');
      if (!html) return;

      grid.innerHTML = html;
      wrap.hidden = false;
      wireRail(grid);
    }).catch(function () { /* leave it hidden, it is not load-bearing */ });
  }

  function cardHTML(p) {
    var variants = p.variants || [];
    var v = variants[0] || null;

    /* /search/suggest.json returns variants: [] - no variant id - so those
       cards link to the product page rather than adding directly. */
    var price = toPaise(v ? v.price : p.price);
    var cmp = toPaise(v ? v.compare_at_price : p.compare_at_price_min);
    var onSale = cmp > price;
    var pct = onSale ? Math.round(((cmp - price) / cmp) * 100) : 0;
    if (!price) return '';

    var img = imageUrlOf(p);

    var soldOut = p.available === false || (v && v.available === false);
    var btn;
    if (soldOut) {
      btn = '<span class="rt-upsell__card-btn rt-upsell__card-btn--ooo">Sold out</span>';
    } else if (v && v.id && variants.length === 1) {
      btn = '<button type="button" class="rt-upsell__card-btn" data-rt-add="' + v.id + '">' +
              '<svg width="11" height="11" viewBox="0 0 12 12" fill="none" aria-hidden="true">' +
              '<path d="M6 2.5v7M2.5 6h7" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/></svg>Add' +
            '</button>';
    } else {
      btn = '<a class="rt-upsell__card-btn" href="/products/' + esc(p.handle) + '">View product</a>';
    }

    return '' +
      '<article class="rt-upsell__card">' +
        '<div class="rt-upsell__card-img-wrap">' +
          '<a class="rt-upsell__card-img-inner" href="/products/' + esc(p.handle) + '" tabindex="-1" aria-hidden="true">' +
            (img ? '<img src="' + esc(img) + '" alt="" loading="lazy" decoding="async" width="176" height="176">' : '') +
          '</a>' +
          (onSale ? '<span class="rt-upsell__card-badge rt-upsell__card-badge--sale">' + pct + '% off</span>' : '') +
        '</div>' +
        '<div class="rt-upsell__card-body">' +
          (p.vendor ? '<span class="rt-upsell__card-vendor">' + esc(p.vendor) + '</span>' : '') +
          '<h3 class="rt-upsell__card-name"><a href="/products/' + esc(p.handle) + '">' + esc(p.title) + '</a></h3>' +
          '<p class="rt-upsell__card-prices">' +
            '<span class="rt-upsell__card-price">' + money(price) + '</span>' +
            (onSale ? '<span class="rt-upsell__card-was">' + money(cmp) + '</span>' : '') +
          '</p>' +
        '</div>' +
        btn +
      '</article>';
  }

  /* -- ranking ------------------------------------------------------------
     The old rail asked Shopify for intent=related and printed whatever came
     back. "Related" means SIMILAR, so a cart holding Brembo front pads got
     seven more sets of brake pads. Nobody needs a second unrelated set; they
     need the discs, the lines, or a filter for the same vehicle.

       +35  merchant-curated complementary product
       +30  came from a same-vehicle search, so it definitely fits their car
       +25  the other axle of the same part for the same vehicle
       +10  per vehicle tag shared with something already in the cart
       +15/10/8/5  proven attachment (plug, air filter, oil filter, oil)
       -45  same kind of part as the cart item it was suggested for

     Curated outranks a search hit because a human chose it. It is the best
     signal on the store now that Search & Discovery has been populated:
     8,404 links across 3,363 products. When this rail was first built that
     endpoint returned nothing, so the heuristics below carried everything.

     Same-category items are demoted rather than dropped, so they still fill
     the rail when there is nothing better. Then cap 2 per vendor so a single
     brand cannot take the whole row. */

  /* Same category as the cart item this candidate was sourced FOR - not as
     anything in the cart. See the note in cartContext(). */
  function isSameCategoryAsGroup(p, ctx) {
    var g = (p.__group == null) ? 0 : p.__group;
    var item = ctx.items[g];
    if (!item || !item.category) return false;
    return categoryOf(p) === item.category;
  }

  /* The one same-category suggestion that IS wanted: the other end of the
     same part, for the same vehicle. Of the last 50 orders, 8 held more than
     one product and 4 of those 8 were exactly this pairing (RT1520 Brembo
     front + rear, RT1514 NGage front + rear, and the pad pairs inside RT1504
     and RT1484). The blanket same-category rule was suppressing the store's
     single most common real co-purchase. */
  function isAxleCounterpart(p, ctx) {
    var g = (p.__group == null) ? 0 : p.__group;
    var item = ctx.items[g];
    if (!item || !item.axle || !item.category) return false;

    var a = axleOf(p);
    if (!a || a === item.axle) return false;
    if (categoryOf(p) !== item.category) return false;

    /* Must fit the SAME vehicle, otherwise it is just another random pad
       set wearing the word "Rear". */
    if (p.__sameVehicle) return true;
    var tags = lowerAll(p.tags);
    return tags.some(function (t) { return item.vehicleTags.indexOf(t) !== -1; });
  }

  /* Same kind of part AND not the counterpart, i.e. genuinely a duplicate of
     what they are already buying. */
  function isRedundant(p, ctx) {
    return isSameCategoryAsGroup(p, ctx) && !isAxleCounterpart(p, ctx);
  }

  function scoreCandidate(p, ctx) {
    var tags = lowerAll(p.tags);
    var sharedVehicle = tags.filter(function (t) {
      return ctx.vehicleTags.indexOf(t) !== -1;
    }).length;

    /* Only bias a consumable the cart does not already contain. Checked
       across the WHOLE cart rather than per group: a second plug is never
       wanted, even for the other vehicle in a two-vehicle basket. */
    var cat = categoryOf(p);
    var bias = (cat && ctx.categories.indexOf(cat) === -1 && ATTACHMENT_BIAS[cat]) || 0;

    return (p.__curated ? 35 : 0) +
           (p.__sameVehicle ? 30 : 0) +
           (isAxleCounterpart(p, ctx) ? 25 : 0) +
           (sharedVehicle * 10) +
           bias -
           (isRedundant(p, ctx) ? 45 : 0);
  }

  /* Pick 8, but make sure every vehicle in the cart is represented.

     Straight score order does not work for a mixed cart: put a Baleno air
     filter and a CB350 shock in the same basket and the bike wins every slot,
     because one vehicle's tags simply score higher than the other's. Measured
     before this change: 8 of 8 cards were CB350 parts and the car owner saw
     nothing. So: sort within each cart item, then deal round-robin. */
  function renderUpsell(pool, ctx) {
    if (!upsellGrid) return;

    /* Two tiers per group.

       PRIMARY  anything that is NOT a duplicate of the cart item it was
                suggested for. The front/rear counterpart counts as primary,
                since that is a real pairing rather than a duplicate.
       RESERVE  same-kind items. Round-robin used to force these in whenever a
                group ran thin - which is how "BMC air filter for Maruti Ciaz"
                appeared under a cart that already held a BMC car air filter.
                They are now last resort only, used to top the rail up. */
    var primary = {}, reserve = {}, order = [];
    pool.forEach(function (p) {
      if (!p || !p.id) return;
      if (ctx.skip.indexOf(String(p.id)) !== -1) return;
      if (p.available === false) return;
      var g = (p.__group == null) ? 0 : p.__group;
      if (!primary[g]) { primary[g] = []; reserve[g] = []; order.push(g); }
      var entry = { p: p, score: scoreCandidate(p, ctx) };
      (isRedundant(p, ctx) ? reserve[g] : primary[g]).push(entry);
    });
    order.sort(function (a, b) { return a - b; });
    order.forEach(function (g) {
      primary[g].sort(function (a, b) { return b.score - a.score; });
      reserve[g].sort(function (a, b) { return b.score - a.score; });
    });
    var groups = primary;

    /* One pass takes at most one card from each cart item, then goes round
       again, so every vehicle in the basket is represented. Stops as soon as
       no group can contribute. */
    var perVendor = {}, picks = [], seen = {};

    function deal(source) {
      var guard = 0;
      while (picks.length < 8 && guard++ < 200) {
        var tookAny = false;
        for (var i = 0; i < order.length && picks.length < 8; i++) {
          var list = source[order[i]] || [];
          while (list.length) {
            var next = list.shift();
            if (seen[next.p.id]) continue;
            var vendor = next.p.vendor || '?';
            perVendor[vendor] = perVendor[vendor] || 0;
            if (perVendor[vendor] >= 2) continue;   /* no brand monopolies */
            perVendor[vendor]++;
            seen[next.p.id] = 1;
            picks.push(next.p);
            tookAny = true;
            break;
          }
        }
        if (!tookAny) break;
      }
    }

    deal(primary);
    /* Only if the rail would look broken. A short rail of useful things beats
       a full one padded with duplicates of what they are already buying. */
    if (picks.length < 4) deal(reserve);

    if (!picks.length) {
      if (upsellWrap) upsellWrap.hidden = true;
      loadSeen([]);
      return;
    }

    var html = picks.map(cardHTML).filter(Boolean).join('');
    if (!html) {
      if (upsellWrap) upsellWrap.hidden = true;
      loadSeen([]);
      return;
    }

    upsellWrap.hidden = false;
    upsellGrid.innerHTML = html;
    wireRail(upsellGrid);

    var count = document.getElementById('rt-upsell-count');
    if (count) { count.textContent = picks.length + ' picks'; count.hidden = false; }

    /* Hand the rail's own picks down, so the same product never appears twice
       on one page. Two rails showing the identical card is worse than one. */
    loadSeen(picks.map(function (p) { return p.id; }));
  }

  function loadUpsell() {
    if (!upsellWrap || !upsellGrid) { loadSeen([]); return; }

    var ctx = cartContext();
    if (!ctx.items.length) return;

    var pool = {};
    /* flags: { sameVehicle, curated }. A product can be claimed by one source
       and then confirmed by another, so both flags UPGRADE an already-pooled
       product rather than being dropped. Knowing a candidate is both curated
       and vehicle-matched is strictly better than knowing only one. */
    function collect(list, flags, group) {
      flags = flags || {};
      (list || []).forEach(function (p) {
        if (!p || !p.id) return;
        if (pool[p.id]) {
          if (flags.sameVehicle) pool[p.id].__sameVehicle = true;
          if (flags.curated) pool[p.id].__curated = true;
          return;
        }
        p.__sameVehicle = !!flags.sameVehicle;
        p.__curated = !!flags.curated;
        p.__group = group;
        pool[p.id] = p;
      });
    }
    function getJSON(url) {
      return fetch(url, { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .catch(function () { return null; });
    }

    /* Up to 3 distinct products get their own sources, so a cart holding parts
       for two vehicles produces suggestions for both. Every request goes out at
       once: chained, three of them already cost ~984 ms; in parallel the whole
       set lands in about the time of the slowest one. */
    var considered = ctx.items.slice(0, 3);
    var jobs = [];

    considered.forEach(function (item, idx) {
      var rec = '/recommendations/products.json?product_id=' + encodeURIComponent(item.id) + '&limit=10&intent=';

      /* Complementary for EVERY cart item, not just the first. It used to be
         requested once because the endpoint returned nothing store-wide;
         it is populated now, so each item deserves its own curated list. */
      jobs.push(getJSON(rec + 'complementary').then(function (d) {
        return { kind: 'comp', group: idx, data: d };
      }));
      jobs.push(getJSON(rec + 'related').then(function (d) {
        return { kind: 'rec', group: idx, data: d };
      }));
      if (item.searchTerm) {
        jobs.push(getJSON('/search/suggest.json?q=' + encodeURIComponent(item.searchTerm) +
                          '&resources[type]=product&resources[limit]=10')
          .then(function (d) {
            return { kind: 'search', group: idx, data: d };
          }));
      }
    });

    Promise.all(jobs)
      .then(function (results) {
        /* Searches first, so vehicle-matched products keep that flag and the
           right __group. Curated second, upgrading anything already pooled.
           Related last, as filler. */
        results.filter(function (r) { return r && r.kind === 'search'; })
          .forEach(function (r) {
            var d = r.data;
            collect(d && d.resources && d.resources.results && d.resources.results.products,
                    { sameVehicle: true }, r.group);
          });
        results.filter(function (r) { return r && r.kind === 'comp'; })
          .forEach(function (r) {
            collect(r.data && r.data.products, { curated: true }, r.group);
          });
        results.filter(function (r) { return r && r.kind === 'rec'; })
          .forEach(function (r) {
            collect(r.data && r.data.products, {}, r.group);
          });

        upsellLoaded = true;
        var all = Object.keys(pool).map(function (k) { return pool[k]; });
        renderUpsell(all, ctx);
      })
      .catch(function () {
        if (upsellWrap) upsellWrap.hidden = true;
        loadSeen([]);
      });
  }

  /* The old code did window.location.reload() 600 ms after adding. */
  function addUpsell(btn) {
    var id = btn.getAttribute('data-rt-add');
    if (!id || btn.classList.contains('is-busy')) return;
    btn.classList.add('is-busy');

    post('/cart/add.js', { items: [{ id: parseInt(id, 10), quantity: 1 }] }, null)
      .then(function () {
        btn.classList.remove('is-busy');
        btn.classList.add('rt-upsell__card-btn--added');
        btn.textContent = 'Added';
        return fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) { return r.json(); });
      })
      .then(function (cart) {
        if (!cart || !SECTION_ID) return;
        /* A brand new line needs real server markup, so this is the one case
           where a section fetch is the honest answer — and it swaps only the
           list and the totals, not the page. */
        return fetch(window.location.pathname + '?section_id=' + encodeURIComponent(SECTION_ID),
                     { credentials: 'same-origin' })
          .then(function (r) { return r.text(); })
          .then(function (html) {
            var doc = new DOMParser().parseFromString(html, 'text/html');
            var freshList = doc.querySelector('[data-rt-list]');
            var here = $('[data-rt-list]');
            if (freshList && here) here.innerHTML = freshList.innerHTML;

            var freshTotals = doc.querySelector('[data-rt-totals]');
            var hereTotals = $('[data-rt-totals]');
            if (freshTotals && hereTotals) hereTotals.innerHTML = freshTotals.innerHTML;

            paintFromCart(cart);
          });
      })
      .catch(function (err) {
        btn.classList.remove('is-busy');
        alertUser((err && err.message) || 'Could not add that item.');
      });
  }

  /* ────────────────────────────────────────────────────────────────── boot ── */

  function boot() {
    /* Self-check: format the exact total Liquid rendered and compare strings.
       If they differ (an unusual money_format, Indian lakh grouping, or a
       currency app rewriting prices) fall back to Intl rather than paint
       wrong numbers into the cart. */
    var renderedTotal = (root.getAttribute('data-totalprice') || '').trim();
    var totalCents = parseInt(root.getAttribute('data-total-cents'), 10);
    if (renderedTotal && !isNaN(totalCents) && money(totalCents) !== renderedTotal) {
      moneyOk = false;
      if (window.console) {
        console.warn('[rt-cart] money_format not reproducible ("' + money(totalCents) +
                     '" vs "' + renderedTotal + '"), using Intl fallback.');
      }
    }

    lines().forEach(function (li) {
      var input = $('[name="updates[]"]', li);
      if (input) syncStepper(li, parseInt(input.value, 10) || 1);
    });

    /* Only arm the unlock celebration after the first authoritative paint, so
       arriving at /cart with three items already in the basket does not fire
       a reward the customer did not just earn. */
    refreshFromServer().then(function () { _booted = true; });
    loadUpsell();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
}());
